import * as React from "react"
import { ChevronDown, Check, Search, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

export interface SelectOption {
  value: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
  /** Render the option but refuse selection. Use when the choice EXISTS for
   *  this user and is unavailable for this particular subject — hiding it
   *  instead would read as "this app cannot do that" (cdb_webos#754). */
  disabled?: boolean
  /** Short muted note on the right of the row. Its job is to answer "why is
   *  this greyed out", so it belongs on the option, not under the field. */
  hint?: string
  /** Options sharing a group render together under the group's heading
   *  (`SelectProps.groupLabels`), indented, after a separator — e.g. the close
   *  reasons under "Geschlossen als …" (cdb_webos#1171). Keep grouped options
   *  next to each other in `options`. */
  group?: string
  /** Small accent pill on the row, e.g. "Vorschlag" for a suggested choice. */
  badge?: string
}

export interface SelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Enable search functionality for large option lists */
  searchable?: boolean
  /** Placeholder text for the search input */
  searchPlaceholder?: string
  /** Maximum height of the dropdown content (default: 300px) */
  maxHeight?: number
  /** Inline style for the trigger, e.g. a colour that depends on the value
   *  (the bug-report status pill, #1130). Wins over the theme classes. */
  style?: React.CSSProperties
  /** Gives every option `data-testid="<prefix><value>"`, so tests can pick an
   *  option of this particular select by value (#1130). */
  optionTestIdPrefix?: string
  /** Headings of the option groups, by `SelectOption.group`. */
  groupLabels?: Record<string, React.ReactNode>
  /** Custom trigger content for the selected option, e.g. "Geschlossen ·
   *  Abgelehnt" for a grouped option whose own label needs its group. */
  renderValue?: (option: SelectOption) => React.ReactNode
  /** On the trigger button, so a `<label htmlFor>` names it (#1170). */
  id?: string
  /** On the trigger button. */
  'data-testid'?: string
}

/** Keeps the option list inside the viewport: the menu flips to the side with
 *  room, and the list never grows taller than what that side offers (#1130). */
const COLLISION_PADDING = 8
function optionListMaxHeight(maxHeight: number, searchable: boolean) {
  // Content padding (p-2) plus, when searchable, the search row.
  const chrome = searchable ? 72 : 24
  return `min(${maxHeight}px, calc(var(--radix-dropdown-menu-content-available-height, ${maxHeight + chrome}px) - ${chrome}px))`
}

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  ({
    value,
    onValueChange,
    options,
    placeholder,
    className,
    disabled,
    searchable = false,
    searchPlaceholder,
    maxHeight = 300,
    optionTestIdPrefix,
    groupLabels,
    renderValue,
    ...props
  }, ref) => {
    const { t } = useTranslation()
    const [searchQuery, setSearchQuery] = React.useState("")
    const [isOpen, setIsOpen] = React.useState(false)

    // Use translated placeholder or empty string as fallback
    const displayPlaceholder = placeholder ?? ''
    const selectedOption = options.find(option => option.value === value)

    // Filter options based on search query
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery.trim()) return options
      const query = searchQuery.toLowerCase()
      return options.filter(option =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query)
      )
    }, [options, searchQuery])

    // Reset search when dropdown closes
    React.useEffect(() => {
      if (!isOpen) {
        setSearchQuery("")
      }
    }, [isOpen])

    const handleSelect = (optionValue: string) => {
      onValueChange(optionValue)
      setIsOpen(false)
    }

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            ref={ref}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-in-out hover:border-ring/50",
              "dark:bg-[var(--menu-bg)] dark:border-[var(--window-border-color)] dark:text-[var(--text-color)] dark:hover:bg-[var(--menu-item-hover)] dark:focus:border-[var(--window-border-color)]",
              className
            )}
            disabled={disabled}
            {...props}
          >
            <span className={cn(
              "truncate flex items-center gap-2",
              !selectedOption && "text-muted-foreground dark:text-[var(--text-secondary)]"
            )}>
              {selectedOption && renderValue ? renderValue(selectedOption) : (
                <>
                  {selectedOption?.icon && (
                    <span className="flex-shrink-0">
                      {React.createElement(selectedOption.icon, { className: "h-4 w-4" })}
                    </span>
                  )}
                  {selectedOption ? selectedOption.label : displayPlaceholder}
                </>
              )}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 transition-transform duration-200 group-data-[state=open]:rotate-180 dark:text-[var(--icon-color)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          collisionPadding={COLLISION_PADDING}
          className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)] backdrop-blur-xl dark:bg-[var(--menu-bg)] dark:border-[var(--window-border-color)] z-[20000]"
        >
          {/* Search input */}
          {searchable && (
            <div className="px-2 py-2 border-b border-border dark:border-[var(--window-border-color)]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-[var(--text-secondary)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder ?? t("common.search")}
                  className={cn(
                    "w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input bg-background",
                    "focus:outline-none focus:ring-1 focus:ring-ring",
                    "dark:bg-[var(--input-bg)] dark:border-[var(--window-border-color)] dark:text-[var(--text-color)]",
                    "placeholder:text-muted-foreground dark:placeholder:text-[var(--text-secondary)]"
                  )}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* Options list with scroll */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: optionListMaxHeight(maxHeight, searchable) }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground dark:text-[var(--text-secondary)] text-center">
                {t("common.noResults")}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const startsGroup = option.group !== undefined && option.group !== filteredOptions[index - 1]?.group
                return (
                <React.Fragment key={option.value}>
                {startsGroup && (
                  <>
                    {index > 0 && <DropdownMenuSeparator />}
                    {groupLabels?.[option.group!] !== undefined && (
                      <DropdownMenuLabel
                        className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-[var(--text-color)]"
                        data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}group-${option.group}` : undefined}
                      >
                        {groupLabels[option.group!]}
                      </DropdownMenuLabel>
                    )}
                  </>
                )}
                <DropdownMenuItem
                  data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}${option.value}` : undefined}
                  disabled={option.disabled}
                  onClick={option.disabled ? undefined : () => handleSelect(option.value)}
                  className={cn(
                    "dark:text-[var(--text-color)]",
                    option.group !== undefined && groupLabels?.[option.group] !== undefined && "pl-8",
                    option.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer dark:hover:bg-[var(--menu-item-hover)] dark:focus:bg-[var(--menu-item-hover)]"
                  )}
                >
                  <div className="flex items-center justify-between w-full gap-3">
                    <span className="flex items-center gap-2 truncate">
                      {option.icon && (
                        <span className="flex-shrink-0">
                          {React.createElement(option.icon, { className: "h-4 w-4" })}
                        </span>
                      )}
                      <span className="truncate">{option.label}</span>
                    </span>
                    <span className="flex flex-shrink-0 items-center gap-2">
                      {option.badge && (
                        <span className="rounded-full bg-[color-mix(in_srgb,var(--theme-interactive-color)_16%,transparent)] px-1.5 py-px text-[11px] font-medium text-[var(--text-color)]">
                          {option.badge}
                        </span>
                      )}
                      {option.hint ? (
                        <span className="text-xs text-muted-foreground dark:text-[var(--text-secondary)]">
                          {option.hint}
                        </span>
                      ) : (
                        value === option.value && (
                          <Check className="h-4 w-4 text-primary dark:text-[var(--icon-color)]" />
                        )
                      )}
                    </span>
                  </div>
                </DropdownMenuItem>
                </React.Fragment>
                )
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)
Select.displayName = "Select"

// =============================================================================
// MultiSelect Component
// =============================================================================

export interface MultiSelectProps {
  values: string[]
  onValuesChange: (values: string[]) => void
  options: SelectOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Enable search functionality for large option lists */
  searchable?: boolean
  /** Placeholder text for the search input */
  searchPlaceholder?: string
  /** Maximum height of the dropdown content (default: 300px) */
  maxHeight?: number
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  ({
    values,
    onValuesChange,
    options,
    placeholder,
    className,
    disabled,
    searchable = false,
    searchPlaceholder,
    maxHeight = 300,
    ...props
  }, ref) => {
    const { t } = useTranslation()
    const [searchQuery, setSearchQuery] = React.useState("")
    const [isOpen, setIsOpen] = React.useState(false)

    const displayPlaceholder = placeholder ?? ''
    const selectedOptions = options.filter(option => values.includes(option.value))

    // Filter options based on search query
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery.trim()) return options
      const query = searchQuery.toLowerCase()
      return options.filter(option =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query)
      )
    }, [options, searchQuery])

    // Reset search when dropdown closes
    React.useEffect(() => {
      if (!isOpen) {
        setSearchQuery("")
      }
    }, [isOpen])

    const handleToggle = (optionValue: string) => {
      if (values.includes(optionValue)) {
        onValuesChange(values.filter(v => v !== optionValue))
      } else {
        onValuesChange([...values, optionValue])
      }
    }

    const handleRemove = (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation()
      onValuesChange(values.filter(v => v !== optionValue))
    }

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            ref={ref}
            className={cn(
              "flex min-h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-in-out hover:border-ring/50",
              "dark:bg-[var(--menu-bg)] dark:border-[var(--window-border-color)] dark:text-[var(--text-color)] dark:hover:bg-[var(--menu-item-hover)] dark:focus:border-[var(--window-border-color)]",
              className
            )}
            disabled={disabled}
            {...props}
          >
            <div className="flex flex-wrap gap-1 flex-1">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground dark:text-[var(--text-secondary)]">
                  {displayPlaceholder}
                </span>
              ) : (
                selectedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    {option.label}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={(e) => handleRemove(option.value, e)}
                    />
                  </Badge>
                ))
              )}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2 dark:text-[var(--icon-color)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          collisionPadding={COLLISION_PADDING}
          className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)] backdrop-blur-xl dark:bg-[var(--menu-bg)] dark:border-[var(--window-border-color)] z-[20000]"
        >
          {/* Search input */}
          {searchable && (
            <div className="px-2 py-2 border-b border-border dark:border-[var(--window-border-color)]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-[var(--text-secondary)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder ?? t("common.search")}
                  className={cn(
                    "w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input bg-background",
                    "focus:outline-none focus:ring-1 focus:ring-ring",
                    "dark:bg-[var(--input-bg)] dark:border-[var(--window-border-color)] dark:text-[var(--text-color)]",
                    "placeholder:text-muted-foreground dark:placeholder:text-[var(--text-secondary)]"
                  )}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* Options list with scroll */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: optionListMaxHeight(maxHeight, searchable) }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground dark:text-[var(--text-secondary)] text-center">
                {t("common.noResults")}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = values.includes(option.value)
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={(e) => {
                      e.preventDefault()
                      handleToggle(option.value)
                    }}
                    className="cursor-pointer dark:text-[var(--text-color)] dark:hover:bg-[var(--menu-item-hover)] dark:focus:bg-[var(--menu-item-hover)]"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="flex items-center gap-2 truncate">
                        {option.icon && (
                          <span className="flex-shrink-0">
                            {React.createElement(option.icon, { className: "h-4 w-4" })}
                          </span>
                        )}
                        <span className="truncate">{option.label}</span>
                      </span>
                      {isSelected && (
                        <Check className="h-4 w-4 flex-shrink-0 text-primary dark:text-[var(--icon-color)]" />
                      )}
                    </div>
                  </DropdownMenuItem>
                )
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)
MultiSelect.displayName = "MultiSelect"

export { Select, MultiSelect }
